import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { QueryStats, Schedule, TrendingUp, SwapHoriz } from '@mui/icons-material';
import { advancedReportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface ConversationAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
  selectedChannel: number | 'all';
}

export const ConversationAnalytics: React.FC<ConversationAnalyticsProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await advancedReportsAPI.getConversationalAnalytics(params);
      setAnalyticsData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversation analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analyticsData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No analytics data available for the selected period.
      </Alert>
    );
  }

  // Prepare data for export
  const exportData = [
    {
      total_conversations: analyticsData.total_conversations,
      avg_duration: analyticsData.avg_conversation_duration,
      messages_per_conversation: analyticsData.messages_per_conversation,
      handoff_rate: analyticsData.handoff_rate
    },
    ...(Array.isArray(analyticsData.peak_hours) ? analyticsData.peak_hours : []),
    ...(Array.isArray(analyticsData.resolution_patterns) ? analyticsData.resolution_patterns : [])
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QueryStats sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Conversation Analytics
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="conversation_analytics"
          metadata={{ dateRange, filters: { agent: selectedAgent, channel: selectedChannel } }}
        />
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Conversations
              </Typography>
              <Typography variant="h4" color="primary">
                {analyticsData.total_conversations.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Conversation Length
              </Typography>
              <Typography variant="h4" color="secondary">
                {analyticsData.avg_conversation_length?.toFixed(1) || '0.0'}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Messages per Conv
              </Typography>
              <Typography variant="h4" color="info.main">
                {analyticsData.avg_messages_per_conversation?.toFixed(1) || '0.0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Handoff Rate
              </Typography>
              <Typography variant="h4" color="warning.main">
                {analyticsData.handoff_analysis?.with_handoff && analyticsData.handoff_analysis?.without_handoff
                  ? ((analyticsData.handoff_analysis.with_handoff /
                     (analyticsData.handoff_analysis.with_handoff + analyticsData.handoff_analysis.without_handoff)) * 100).toFixed(1)
                  : '0.0'}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Peak Hours */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                Peak Hours Analysis
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hour</TableCell>
                      <TableCell align="right">Conversations</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.peak_hours?.map((hour: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{hour.hour}:00</TableCell>
                        <TableCell align="right">{hour.count || 0}</TableCell>
                        <TableCell align="right">
                          {analyticsData.total_conversations && hour.count
                            ? ((hour.count / analyticsData.total_conversations) * 100).toFixed(1)
                            : '0.0'}%
                        </TableCell>
                      </TableRow>
                    )) || (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                Resolution Patterns
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(analyticsData.resolution_patterns).map(([pattern, count]) => (
                  <Box key={pattern} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {pattern.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {count as number}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conversation Flow Analysis */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SwapHoriz sx={{ mr: 1, verticalAlign: 'middle' }} />
                Handoff Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Conversations with Handoff</Typography>
                  <Typography color="warning.main" variant="h6">
                    {analyticsData.handoff_analysis.with_handoff}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Single Agent Conversations</Typography>
                  <Typography color="success.main" variant="h6">
                    {analyticsData.handoff_analysis.without_handoff}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Message Flow Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Avg Customer Messages</Typography>
                  <Typography color="primary" variant="h6">
                    {analyticsData.conversation_flow_analysis?.avg_customer_messages?.toFixed(1) || '0.0'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Avg Agent Messages</Typography>
                  <Typography color="secondary" variant="h6">
                    {analyticsData.conversation_flow_analysis?.avg_agent_messages?.toFixed(1) || '0.0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};